import { FormikProps } from "formik";
import { ChangeEvent, useCallback, useState } from "react";
import { getCustomers } from "../../api/config";
import { useFetch } from "../../hooks/useFetch";
import { ChimeConfigInitValue, SelectOption } from "../../interfaces/fe.interfaces";
import { ReactComponent as UploadFileIcon } from "../../../assets/images/uploadFile.svg";
import { GetCustomersResponse } from "../../interfaces/be.interfaces";
import Select from "../Select";
import Spinner from "../Spinner";
import styles from "./styles.module.scss";
import Checkbox from "../Checkbox";

/**
 * Common form component for Add or edit chime config
 * Takes in all the props from Formik component
 */
const Form = ({
  values,
  handleChange,
  touched,
  errors,
  handleSubmit,
  handleReset,
  setFieldValue,
  handleBlur,
  isSubmitting,
  enableReinitialize,
  setFieldTouched,
}: FormikProps<ChimeConfigInitValue>) => {
  const [customerList, setCustomerList] = useState<SelectOption<string | null>[]>([]);

  /**
   * Fetch list of customers to show in dropdown
   * only active customers are displayed in dropdown
   */
  const { loading: customerInfoLoading } = useFetch<GetCustomersResponse>({
    runOnMount: true,
    url: getCustomers,
    onSuccess: (response) => {
      // Prepare customer data in option format to show in dropdown and filter inactive customers
      const customerOptions: SelectOption<string>[] = response.data?.data?.customers
        ?.filter((item) => item.is_enable)
        .map((customer) => ({
          label: customer.customer_name,
          value: customer.vendor_id,
        }));

      setCustomerList(customerOptions);
    },
  });

  // Handle customer change
  const onChangeCustomer = useCallback((value: number) => {
    setFieldValue("vendor_id", value);
  }, []);

  const onFileChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    setFieldTouched("chime_config_bin", true);
    setFieldValue("chime_config_bin", event.target.files?.[0]);
  }, []);

  const onChangeVersion = useCallback((value: string) => {
    const regEx = /^[0-9.]+$/;

    // If value is empty or number or .
    if (regEx.test(value) || value === "") setFieldValue("chime_version", value);
  }, []);

  return (
    <form onSubmit={handleSubmit}>
      <div className={styles.dialog}>
        {isSubmitting || customerInfoLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <input
                  id="chime_version"
                  name="chime_version"
                  type="text"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChangeVersion(e?.target?.value)}
                  placeholder="Add a config version"
                  value={values.chime_version}
                />
                {errors?.chime_version && touched.chime_version ? <p className={styles.error}>{errors?.chime_version}</p> : null}
              </div>
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <input
                  name="chime_audio"
                  type="text"
                  placeholder={"Add a chime audio"}
                  value={values.chime_audio}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
                {errors?.chime_audio && touched.chime_audio ? <p className={styles.error}>{errors?.chime_audio}</p> : null}
              </div>
            </div>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <Select
                  selectProps={{
                    value: values.vendor_id,
                    name: "vendor_id",
                    placeholder: "Select Customer",
                    onBlur: handleBlur,
                    onChange: (event) => onChangeCustomer(+event.target.value),
                  }}
                  options={customerList}
                />
                {errors?.vendor_id && touched?.vendor_id ? <p className={styles.error}>{errors.vendor_id}</p> : null}
              </div>
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <textarea
                  name="chime_description"
                  placeholder={"Add a chime description"}
                  value={values.chime_description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  autoComplete="off"
                />
                {errors?.chime_description && touched.chime_description ? (
                  <p className={styles.error}>{errors?.chime_description}</p>
                ) : null}
              </div>
            </div>
            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <div className={styles.wisaFirmwareUploadFormGridItem}>
                  <p className={styles.wisaFirmwareUploadFormIconText}>Choose Wav file</p>
                  <label className={styles.wisaFirmwareUploadFormIconLabel} htmlFor="chime_config_bin">
                    <input id="chime_config_bin" name="chime_config_bin" type="file" onChange={onFileChange} hidden />
                    <UploadFileIcon className={styles.wisaFirmwareUploadFormUploadIcon} />
                    <div className={styles.wisaFirmwareUploadFormUploadMessagesWrapper}>
                      {values.chime_config_bin?.name && (
                        <p className={styles.wisaFirmwareUploadFormFileName}>{values.chime_config_bin?.name}</p>
                      )}
                      {touched.chime_config_bin && errors.chime_config_bin && (
                        <span className={styles.error}>{errors.chime_config_bin}</span>
                      )}
                    </div>
                  </label>
                </div>
              </div>
            </div>

            <div className="w-100 d-flex justify-content-around">
              <div className={`${styles.inputContainer} d-flex justify-content-between align-item-center`}>
                <Checkbox
                  label="Chime configuration is current customer version?"
                  name="current_customer_version"
                  checked={values.current_customer_version}
                  disabled={enableReinitialize}
                  onChange={() => !enableReinitialize && setFieldValue("current_customer_version", !values.current_customer_version)}
                />
              </div>
            </div>

            <div className="w-100 d-flex justify-content-around">
              <button className={styles.dialogBtn} type="submit" disabled={isSubmitting}>
                {enableReinitialize ? "Update" : "Add"}
              </button>
              <button type="button" className={styles.dialogBtn} onClick={handleReset}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </form>
  );
};

export default Form;
